<template>
  <img 
    v-if="finalImageUrl"
    :src="finalImageUrl" 
    :alt="finalAltText"
    :class="imageClass"
    @error="handleError"
    @load="handleLoad"
    v-bind="$attrs"
  />
  <div 
    v-else-if="showPlaceholder"
    :class="['image-placeholder', imageClass]"
    :style="placeholderStyle"
  >
    <div class="placeholder-content">
      <div class="placeholder-icon">📷</div>
      <div class="placeholder-text">{{ placeholderText }}</div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch, PropType } from 'vue'
import { useImageErrorHandler, PLACEHOLDER_IMAGES } from '../composables/useImageErrorHandler'

export default defineComponent({
  name: 'SafeImage',
  inheritAttrs: false,
  props: {
    src: {
      type: String as PropType<string | null>,
      default: null
    },
    alt: {
      type: String,
      default: ''
    },
    fallbackSrc: {
      type: String as PropType<string | null>,
      default: null
    },
    placeholder: {
      type: String,
      default: PLACEHOLDER_IMAGES.GENERAL
    },
    placeholderText: {
      type: String,
      default: 'Image not available'
    },
    showPlaceholder: {
      type: Boolean,
      default: true
    },
    imageClass: {
      type: String,
      default: ''
    },
    // GraphQL image data support
    imageData: {
      type: Object,
      default: null
    }
  },
  setup(props) {
    const { 
      getSafeImageUrl, 
      handleImageError, 
      handleImageLoad,
      getImageUrl,
      getImageAlt 
    } = useImageErrorHandler()
    
    const hasErrored = ref(false)

    // Compute the source URL from either direct src prop or GraphQL imageData
    const sourceUrl = computed(() => {
      if (props.imageData) {
        return getImageUrl(props.imageData)
      }
      return props.src
    })

    // Compute the alt text from either direct alt prop or GraphQL imageData
    const sourceAlt = computed(() => {
      if (props.imageData) {
        return getImageAlt(props.imageData, props.alt)
      }
      return props.alt
    })

    // Determine the final image URL to use
    const finalImageUrl = computed(() => {
      if (hasErrored.value) {
        // If primary source errored, try fallback
        if (props.fallbackSrc) {
          return getSafeImageUrl(props.fallbackSrc)
        }
        // If no fallback or fallback also errored, try placeholder
        if (props.showPlaceholder && props.placeholder) {
          return getSafeImageUrl(props.placeholder)
        }
        return null
      }
      
      // Use primary source if available and safe
      return getSafeImageUrl(sourceUrl.value || undefined, props.fallbackSrc || undefined)
    })

    // Final alt text
    const finalAltText = computed(() => {
      return sourceAlt.value || props.placeholderText
    })

    // Placeholder styling
    const placeholderStyle = computed(() => ({
      aspectRatio: '1.2',
      backgroundColor: '#f3f4f6',
      border: '2px dashed #d1d5db',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '200px'
    }))

    // Reset error state when source changes
    watch(sourceUrl, () => {
      hasErrored.value = false
    })

    const handleError = () => {
      if (sourceUrl.value) {
        handleImageError(sourceUrl.value)
      }
      hasErrored.value = true
    }

    const handleLoad = () => {
      if (sourceUrl.value) {
        handleImageLoad(sourceUrl.value)
      }
      hasErrored.value = false
    }

    return {
      finalImageUrl,
      finalAltText,
      placeholderStyle,
      handleError,
      handleLoad
    }
  }
})
</script>

<style scoped>
.image-placeholder {
  color: #6b7280;
  font-family: system-ui, -apple-system, sans-serif;
}

.placeholder-content {
  text-align: center;
}

.placeholder-icon {
  font-size: 2rem;
  margin-bottom: 0.5rem;
  opacity: 0.5;
}

.placeholder-text {
  font-size: 0.875rem;
  font-weight: 500;
  opacity: 0.7;
}

.image-placeholder:hover {
  background-color: #f9fafb;
  border-color: #9ca3af;
}
</style>
